/* eslint-disable max-len */
/* eslint-disable no-console */
import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useParams, useLocation } from "react-router";
import { v4 as uuidv4 } from "uuid";
import { Box } from "@mui/material";
import { parseUserAgent } from "react-device-detect";
import { useRoleplay } from "@nualang/nualang-api-and-queries/Queries/roleplays";
import RoleplayExercise from "@nualang/nualang-ui-components/dist/Exercises/Roleplay";
import {
  courses as courseQuerys,
  progress as progressQuerys,
  completions as completionQuerys,
  users as userQuerys,
  classrooms as classroomQuerys,
  assignments as assignmentQuerys,
} from "@nualang/nualang-api-and-queries/Queries";
import useServices from "../../../hooks/useServices";
import {
  getCourseSectionTopicProgress,
  getRoleplayProgress,
  // getRoleplayGamesProgress,
  createProgress as createProgressAPI,
  deleteRoleplayProgress as deleteRoleplayProgressAPI,
  deleteCourseSectionTopicProgress as deleteCourseSectionTopicProgressAPI,
} from "@nualang/nualang-api-and-queries/APIs/Progress";
import { reportProblem as reportProblemAPI } from "@nualang/nualang-api-and-queries/APIs/Notifications";
import {
  createCompletion as createCompletionAPI,
  getCourseSectionTopicCompletions,
} from "@nualang/nualang-api-and-queries/APIs/Completions";
import { getCourse } from "@nualang/nualang-api-and-queries/APIs/Courses";
import {
  highlightSpeechText,
  stopHighlightingSpeechText,
} from "../../../utils/index";
import { getUser } from "@nualang/nualang-api-and-queries/APIs/Users";
import { getRoleplay } from "@nualang/nualang-api-and-queries/APIs/Roleplays";
import { getAssignment } from "@nualang/nualang-api-and-queries/APIs/Assignments";
import { getClassroom } from "@nualang/nualang-api-and-queries/APIs/Classrooms";
import { getClassroomMember } from "@nualang/nualang-api-and-queries/APIs/ClassroomMembers";
import {
  UploadRoleplayAudio,
  UploadReportProblemAudio,
} from "../../../utils/audioUpload";
import {
  courses as courseHelpers,
  progress as progressHelpers,
  completions as completionHelpers,
  roleplays as roleplayHelpers,
  classrooms as classroomHelpers,
  notifications as notificationHelpers,
} from "../../../utils/helpers";
import useAudioPlayer from "../../../hooks/useAudioPlayer";
import { useQueryClient } from "@tanstack/react-query";
import {
  trackRecommendedEvent,
  getForLangCharacters,
  searchStringToObj,
} from "../../../utils/index";
// styles
import config from "../../../config";
import { UploadReportProblemImage } from "../../../utils/imageUpload";

import logger from "../../../logger";
import { useApp } from "../../../hooks";

const formatRoleplayProgress = (topicProgress = [], roleplayId, exercise) => {
  const exerciseIndex = topicProgress.findIndex(
    (p) => p.roleplayId === roleplayId && p.exercise === exercise,
  );
  if (exerciseIndex > -1) {
    return {
      existingMessages: topicProgress[exerciseIndex].existingMessages,
      existingAnswerAttempts: topicProgress[exerciseIndex].answerAttempts,
      selectedActor: topicProgress[exerciseIndex].selectedActor,
      exerciseStartTime: topicProgress[exerciseIndex].exerciseStartTime,
    };
  }
  return {
    existingMessages: null,
    existingAnswerAttempts: null,
    selectedActor: null,
    exerciseStartTime: null,
  };
};

const gameList = [
  "roleplay",
  "roleplay-story",
  "roleplay-fill-in-the-blanks",
  "roleplay-act-it-out",
  "roleplay-act-it-out-listening",
  "roleplay-act-it-out-speaking",
  "roleplay-act-it-out-listening-speaking",
];

const formatRoleplayGamesProgress = (topicProgress = [], roleplayId) => {
  const gameProgress = gameList.reduce((gameProgressObj, currentGame) => {
    gameProgressObj[currentGame] = formatRoleplayProgress(
      topicProgress,
      roleplayId,
      currentGame,
    );
    return gameProgressObj;
  }, {});
  return gameProgress;
};

function PlayRoleplay(props) {
  const { t } = useTranslation();
  const [tts, translate] = useServices();
  const [roleplayCreatedAtId, setRoleplayCreatedAtId] = useState("");
  const [disableViewReport, setDisableViewReport] = useState(true);
  const [timeoutArray, setTimeoutArray] = useState([]);
  const location = useLocation();
  const searchParams = searchStringToObj(location.search);
  const assignmentId = searchParams.assignmentId;
  const gameType = searchParams.game || "";
  const formattedGameType = gameType.replace(/-/g, " ");

  const {
    authData: { username, attributes },
    navigate,
    correctSound,
    incorrectSound,
    authenticated,
    handleSignIn,
  } = props;

  const params = useParams();
  const { courseId, sectionId, topicId, roleplayId, classroomId } = params;
  const queryClient = useQueryClient();

  const { sendExerciseCompletionEmail } = notificationHelpers;

  const {
    openSnackbar,
    handleShareRoleplay,
    user,
    startLoading,
    stopLoading,
    markCommunityAccessRestricted,
  } = useApp();
  const audioPlayer = useAudioPlayer();
  const { addToQueue, toggleMuteSound, clearQueue, stop } = audioPlayer;

  const reportProblem = async (body) => {
    try {
      startLoading();
      await reportProblemAPI(body);
      stopLoading();
      openSnackbar("problem_reported", "success");
    } catch (error) {
      logger.error(error);
      stopLoading();
      openSnackbar("problem", "error");
    }
  };

  const parentClassroomQuery = classroomQuerys.useClassroom(
    async () => {
      const classroom = await getClassroom(classroomId);
      const formattedClassroom = await classroomHelpers.formatClassroom(
        classroom.Item,
        false,
        props.authData,
      );
      return formattedClassroom;
    },
    {
      classroomId,
      username,
    },
    { enabled: !!classroomId },
  );

  const parentClassroom = useMemo(
    () =>
      parentClassroomQuery.isSuccess &&
      parentClassroomQuery.data &&
      parentClassroomQuery.data.classroomId
        ? parentClassroomQuery.data
        : {},
    [parentClassroomQuery.data, parentClassroomQuery.isSuccess],
  );

  // Fallback signal: this classroom may already be flagged by the backend as
  // community-access-restricted before the user's own record ever gets
  // flagged (see appAtoms.js). Learn about it as soon as we see it.
  useEffect(() => {
    markCommunityAccessRestricted([parentClassroom]);
  }, [parentClassroom, markCommunityAccessRestricted]);

  const { courseSettings } = parentClassroom;

  const classroomMemberQuery = classroomQuerys.useClassroomMember(
    async () => {
      const member = await getClassroomMember(classroomId, username);
      return member;
    },
    {
      classroomId,
      username,
    },
    { enabled: !!classroomId },
  );

  const classroomMember =
    classroomMemberQuery.isSuccess &&
    classroomMemberQuery.data &&
    classroomMemberQuery.data.Item
      ? classroomMemberQuery.data.Item
      : {};

  const classroomAssignmentQuery = assignmentQuerys.useAssignment(
    async (classroomId, assignmentId) => {
      const assignment = await getAssignment(classroomId, assignmentId);
      return assignment.Item;
    },
    {
      classroomId,
      assignmentId,
    },
    { enabled: !!assignmentId },
  );

  const assignment =
    classroomAssignmentQuery.isSuccess && classroomAssignmentQuery.data
      ? classroomAssignmentQuery.data
      : [];

  const { uniqueAssignmentCoursesIds } = useMemo(() => {
    const courses = new Set();

    assignment?.exercises?.forEach(({ courseSectionTopicId }) => {
      if (courseSectionTopicId) {
        const [courseId] = courseSectionTopicId.split("|");
        courses.add(courseId);
      }
    });

    return {
      uniqueAssignmentCoursesIds: [...courses],
    };
  }, [assignment?.exercises]);

  // Mutations
  const createProgressMutation = progressQuerys.useCreateProgress(
    createProgressAPI,
    {
      onSuccess: () => {
        progressHelpers.invalidateProgressCache({
          courseId,
          sectionId,
          topicId,
          exercise: "roleplay",
          roleplayId,
          memberId: username,
        });
        queryClient.invalidateQueries({
          queryKey: ["roleplays", "inProgress", username],
        });
      },
      onError: () => {
        openSnackbar("problem", "error");
      },
    },
  );
  const deleteTopicProgressMutation = progressQuerys.useDeleteTopicProgress(
    deleteCourseSectionTopicProgressAPI,
    {
      onSuccess: async (data, variables) => {
        await progressHelpers.invalidateProgressCache({
          courseId: variables.courseId,
          sectionId: variables.sectionId,
          topicId: variables.topicId,
          exercise: variables.exercise,
          memberId: variables.memberId,
        });
        if (variables.courseId && variables.sectionId && variables.topicId) {
          queryClient.invalidateQueries({
            queryKey: progressQuerys.progressKeys.listTopicMember(
              variables.courseId,
              variables.sectionId,
              variables.topicId,
              variables.memberId,
              null,
            ),
          });
        }
      },
      onError: () => {
        // allow to fail without showing snackbar
        // openSnackbar('problem', 'error');
      },
    },
  );
  const deleteRoleplayProgressMutation =
    progressQuerys.useDeleteRoleplayProgress(deleteRoleplayProgressAPI, {
      onSuccess: async (data, variables) => {
        await progressHelpers.invalidateProgressCache({
          courseId,
          sectionId,
          topicId,
          roleplayId: variables.roleplayId,
          exercise: "roleplay",
          memberId: variables.memberId,
        });
        if (variables.roleplayId) {
          queryClient.invalidateQueries({
            queryKey: progressQuerys.progressKeys.listRoleplayMember(
              variables.roleplayId,
              variables.memberId,
              null,
            ),
          });
        }
      },
      onError: () => {
        // allow to fail without showing snackbar
        // openSnackbar('problem', 'error');
      },
    });
  const [completedAttempt, setCompletedAttempt] = useState(null);

  const createCompletionMutation = completionQuerys.useCreateTopicCompletion(
    createCompletionAPI,
    {
      onSuccess: async (data, variables) => {
        queryClient.invalidateQueries({
          queryKey: ["roleplays", "completed", username],
        });
        sendCompletionEmail();
        let roleplaySecId = data.Item.createdAt;
        setRoleplayCreatedAtId(roleplaySecId);
        const { exercise = variables.values.exercise, createdBy, createdAt } = data.Item;
        setCompletedAttempt({ exercise, memberId: createdBy, createdAt });
        setDisableViewReport(false);
      },
      onError: () => {
        openSnackbar("problem", "error");
      },
    },
  );

  const roleplayQuery = useRoleplay(
    async () => {
      const roleplay = await getRoleplay(roleplayId);
      const formattedRoleplay = await roleplayHelpers.formatRoleplay(
        roleplay.Item,
        username,
      );
      return formattedRoleplay;
    },
    {
      roleplayId,
    },
  );
  let roleplay = roleplayQuery;

  const userQuery = userQuerys.useUser(
    async (userId) => {
      const response = await getUser(userId, true);
      return response;
    },
    {
      userId: roleplay && roleplay.data && roleplay.data.createdBy,
    },
    {
      enabled: !!(roleplay && roleplay.data && roleplay.data.createdBy),
    },
  );
  const creatorUserAttributes =
    userQuery.isSuccess && userQuery.data && userQuery.data.UserAttributes
      ? userQuery.data.UserAttributes
      : {};

  const courseSectionTopicProgressQuery = progressQuerys.useTopicMemberProgress(
    async (courseId, sectionId, topicId, memberId, returnvalues) => {
      const memberProgress = await getCourseSectionTopicProgress(
        courseId,
        sectionId,
        topicId,
        memberId,
        returnvalues,
      );
      const formattedProgress = await progressHelpers.formatProgress(
        memberProgress.Items,
        username,
        attributes.preferred_username,
      );
      return formattedProgress;
    },
    {
      courseId,
      sectionId,
      topicId,
      memberId: username,
      returnvalues: null,
    },
    {
      enabled:
        !!courseId && !!sectionId && !!topicId && !!username && !!attributes,
    },
  );

  const roleplayProgressQuery = progressQuerys.useRoleplayMemberProgress(
    async (roleplayId, memberId, filters) => {
      const response = await getRoleplayProgress(roleplayId, memberId, filters);
      const formattedProgress = await progressHelpers.formatProgress(
        response.Items,
        memberId,
        attributes.preferred_username,
      );
      return formattedProgress;
    },
    {
      roleplayId,
      memberId: username,
      filters: null,
    },
    {
      enabled: !!roleplayId && !!username && !courseId,
    },
  );

  const roleplayProgressProps = useMemo(
    () =>
      formatRoleplayGamesProgress(
        courseId
          ? courseSectionTopicProgressQuery.data
          : roleplayProgressQuery.data,
        roleplay && roleplay.data ? roleplay.data.roleplayId : null,
      ),
    [
      courseId,
      courseSectionTopicProgressQuery.data,
      roleplay,
      roleplayProgressQuery.data,
    ],
  );

  const [isRoleplayOpen, setIsRoleplayOpen] = useState(true);
  const handleCloseRoleplay = () => {
    setIsRoleplayOpen(false);
    if (courseId && sectionId && topicId) {
      if (classroomId) {
        navigate(
          `/classrooms/${classroomId}/${courseId}/${sectionId}/${topicId}`,
        );
      } else {
        navigate(`/courses/${courseId}/${sectionId}/${topicId}`);
      }
    } else {
      navigate("/roleplays");
    }
  };

  const courseQuery = courseQuerys.useCourse(
    async (cId) => {
      const course = await getCourse(cId);
      const formattedCourse = await courseHelpers.formatCourse(
        course.Item,
        props.authData,
      );
      return formattedCourse;
    },
    {
      courseId,
      username: props.authData.username,
    },
    { enabled: !!courseId },
  );

  const course =
    courseQuery.isSuccess && courseQuery.data && courseQuery.data.courseId
      ? courseQuery.data
      : {};

  const { collaborators, forLang = "" } = course;

  const removeSpecialCharactersfromBlanks = (text) => {
    const searchItem = "_____";
    const ttsText = [];
    text.split(" ").forEach((word, i) => {
      if (word.includes(searchItem)) {
        ttsText[i] = word.replace(/\W/g, "");
      } else {
        ttsText[i] = word;
      }
    });
    return ttsText.join(" ");
  };

  const handleSpeak = async (params) => {
    try {
      const {
        text,
        learnLanguage,
        voiceName,
        mute,
        callback,
        textContainer,
        languageCode,
        voiceSpeed,
        isHoverText = false,
        pitch,
      } = params;
      const textFortts = removeSpecialCharactersfromBlanks(text);
      const result = await tts(
        textFortts,
        learnLanguage,
        voiceName,
        languageCode,
        voiceSpeed,
        pitch,
      );
      if (!mute && result && result.audioUrl) {
        const highlightCallback = () =>
          highlightSpeechText(
            textContainer,
            result.speechMarks,
            setTimeoutArray,
          );
        addToQueue(result.audioUrl, callback, highlightCallback, isHoverText);
      } else if (result.errorMessage) {
        throw new Error(result.errorMessage);
      }
      return result;
    } catch (error) {
      logger.error(error);
      return null;
    }
  };

  const handleStopSpeaking = async (textContainer) => {
    try {
      stopHighlightingSpeechText(textContainer, timeoutArray);
      stop();
      clearQueue();
    } catch (error) {
      logger.error(error);
    }
  };

  const ttsWithoutSpeak = async (params) => {
    try {
      const { text, learnLanguage, voiceName, languageCode, voiceSpeed } =
        params;
      const result = await tts(
        text,
        learnLanguage,
        voiceName,
        languageCode,
        voiceSpeed,
      );
      if (result.errorMessage) {
        throw new Error(result.errorMessage);
      }
      return result;
    } catch (error) {
      logger.error(error);
      return null;
    }
  };

  const handleEditRoleplay = async (roleplay) => {
    const { courseSectionTopicId, roleplayId } = roleplay;
    const idValues = courseSectionTopicId
      ? courseSectionTopicId.split("|")
      : [];
    if (idValues && idValues.length === 3) {
      const courseId = idValues[0].trim();
      const sectionId = idValues[1].trim();
      const topicId = idValues[2].trim();
      if (classroomId) {
        navigate(
          `/classrooms/${classroomId}/${courseId}/${sectionId}/${topicId}/roleplays/${roleplayId}/edit`,
        );
      } else {
        navigate(
          `/courses/${courseId}/${sectionId}/${topicId}/roleplays/${roleplayId}/edit`,
        );
      }
    } else {
      navigate(`/roleplays/${roleplayId}/edit`);
    }
  };

  const handleReportProblem = async (
    problem,
    shareAudio,
    audioBlob,
    shareScreenshot,
    screenshot,
  ) => {
    try {
      const { type, description, currentPhrase, isQuestion } = problem;
      const isCreatorReceivingReportedProblem = [true, undefined].includes(
        creatorUserAttributes?.email_notification?.student_reports_problem,
      );
      const isTeacher = user
        ? user.attributes["custom:role"] === "teach"
        : false;

      let recipients;
      if (type === "equipment_problem") {
        const teacherUsername = parentClassroom && parentClassroom.createdBy;
        recipients = [teacherUsername];
      } else {
        recipients =
          (isCreatorReceivingReportedProblem || isTeacher) &&
          type === "content_problem"
            ? [creatorUserAttributes.sub, config.supportEmail, ...collaborators]
            : [config.supportEmail];
        console.log({
          creatorUserAttributes,
          collaborators,
          config,
          recipients,
        });
      }

      const problemType = (() => {
        switch (type) {
          case "equipment_problem":
            return "Equipment Problem";
          case "technical_problem":
            return "Technical Problem";
          case "content_problem":
            return "Content Problem";
          default:
            return "Other Problem";
        }
      })();

      const [audioUrl, screenshotUrl] = await Promise.all([
        shareAudio && audioBlob
          ? UploadReportProblemAudio(audioBlob, username)
          : Promise.resolve(null),
        shareScreenshot && screenshot
          ? UploadReportProblemImage(screenshot, username)
          : Promise.resolve(null),
      ]);

      const body = {
        recipients,
        params: {
          exerciseLink: `${window.location.origin}${window.location.pathname}`,
          exerciseName: `${roleplay?.data?.roleplayName} - scenario ${formattedGameType}`,
          studentName:
            attributes && attributes.preferred_username
              ? attributes.preferred_username
              : "",
          problemDescription: description,
          problemType: problemType,
          email: attributes ? attributes.email : "",
          username: attributes ? attributes["preferred_username"] : "",
          role: attributes ? attributes["custom:role"] : "",
          browser: JSON.stringify(
            parseUserAgent(window.navigator.userAgent).browser,
          ),
          os: JSON.stringify(parseUserAgent(window.navigator.userAgent).os),
          isWaysideUser: username?.startsWith("Wayside"),
          phrase: isQuestion
            ? `${currentPhrase} (Question)`
            : currentPhrase === "N/A"
              ? currentPhrase
              : `${currentPhrase} (Phrase)`,
          ...(audioUrl && { audioUrl }),
          ...(screenshotUrl && { screenshotUrl }),
        },
      };

      await reportProblem(body);
    } catch (error) {
      logger.error(error);
    }
  };

  const sendCompletionEmail = async () => {
    try {
      if (roleplay?.data?.createdBy) {
        if (
          creatorUserAttributes &&
          creatorUserAttributes.email_notification &&
          creatorUserAttributes.email_notification
            .student_completes_exercise === true &&
          attributes.username !== roleplay?.data?.createdBy
        ) {
          let url;
          const studentName =
            attributes && attributes.preferred_username
              ? attributes.preferred_username
              : "";
          const { courseSectionTopicId } = roleplay.data;
          const idValues = courseSectionTopicId
            ? courseSectionTopicId.split("|")
            : [];
          if (idValues && idValues.length === 3) {
            const courseId = idValues[0].trim();
            const sectionId = idValues[1].trim();
            const topicId = idValues[2].trim();
            url = `${window.location.origin}/courses/${courseId}/${sectionId}/${topicId}/activity/roleplay?roleplayId=${roleplayId}`;
          } else {
            url = `${window.location.href}/activity`;
          }
          sendExerciseCompletionEmail(
            [roleplay?.data?.createdBy],
            studentName,
            creatorUserAttributes && creatorUserAttributes.preferred_username
              ? creatorUserAttributes.preferred_username
              : "",
            roleplay?.data?.roleplayName,
            "Roleplay",
            url,
          );
        }
      }
    } catch (error) {
      logger.error(error);
    }
  };

  const handleSaveProgress = (values) => {
    createProgressMutation.mutate({ values });
  };

  const handleCreateCompletion = (values) => {
    setRoleplayCreatedAtId("");
    setCompletedAttempt(null);
    setDisableViewReport(true);
    const classroomCourseIds =
      parentClassroom?.courses?.length > 0
        ? parentClassroom.courses.toString()
        : null;
    const classroomAssignedCourseIds =
      parentClassroom?.assignedCourses?.length > 0
        ? parentClassroom.assignedCourses.toString()
        : null;
    createCompletionMutation.mutate({
      values,
      roleplayId,
      classroomCourseIds,
      classroomAssignedCourseIds,
      uniqueAssignmentCoursesIds,
    });
  };

  const completedAttemptPath = completedAttempt
    ? completionHelpers.getAttemptPath({
      classroomId,
      courseId,
      sectionId,
      topicId,
      ...completedAttempt,
    })
    : null;

  const courseSectionTopicCompletionsQuery =
    completionQuerys.useTopicMemberCompletions(
      async (courseId, sectionId, topicId, memberId, returnvalues) => {
        const memberCompletions = await getCourseSectionTopicCompletions(
          courseId,
          sectionId,
          topicId,
          memberId,
          returnvalues,
        );
        const formattedCompletions = await completionHelpers.formatCompletions(
          memberCompletions.Items,
          username,
          attributes.preferred_username,
        );
        return formattedCompletions;
      },
      {
        courseId,
        sectionId,
        topicId,
        memberId: username,
        returnvalues: null,
      },
      { enabled: !!username && !!courseId && !!sectionId && !!topicId },
    );

  const roleplayCompletions =
    courseSectionTopicCompletionsQuery.isSuccess &&
    Array.isArray(courseSectionTopicCompletionsQuery.data) &&
    courseSectionTopicCompletionsQuery.data.length
      ? courseSectionTopicCompletionsQuery.data.filter(
          (completion) => completion.roleplayId === roleplayId,
        )
      : [];

  const handleDeleteTopicProgress = async (
    courseId,
    sectionId,
    topicId,
    exercise,
    ids,
  ) => {
    deleteTopicProgressMutation.mutate({
      courseId,
      sectionId,
      topicId,
      exercise,
      memberId: username,
      roleplayId: typeof ids === "object" && ids !== null ? ids.roleplayId : ids,
    });
  };

  const handleDeleteRoleplayProgress = async (roleplayId, exercise) => {
    deleteRoleplayProgressMutation.mutate({
      roleplayId,
      exercise,
      memberId: username,
    });
  };

  const uploadAudioAttempt = async (audioBlob) => {
    const url = await UploadRoleplayAudio(
      audioBlob,
      `${roleplayId}_${uuidv4()}`,
    );
    return url;
  };

  const learnLang = roleplay?.isSuccess ? (roleplay.data.learnLang ?? {}) : {};
  const learnLangCharacters =
    learnLang && Object.keys(learnLang).length !== 0
      ? config.languages[learnLang.toLowerCase()].characters
      : [];
  const forLangCharacters = getForLangCharacters(forLang);

  return (
    <Box>
      {roleplay &&
        roleplay.isSuccess &&
        roleplay.data &&
        (!authenticated ||
          courseSectionTopicProgressQuery.isSuccess ||
          roleplayProgressQuery.isSuccess) && (
          <RoleplayExercise
            t={t}
            roleplay={
              roleplay && roleplay.isSuccess && roleplay.data
                ? roleplay.data
                : {}
            }
            handleClose={handleCloseRoleplay}
            open={isRoleplayOpen}
            learnLangCharacters={learnLangCharacters}
            forLangCharacters={forLangCharacters}
            isRoleplayHidden={false}
            handleSaveProgress={handleSaveProgress}
            handleDeleteProgress={handleDeleteTopicProgress}
            handleDeleteRoleplayProgress={handleDeleteRoleplayProgress}
            handleEditRoleplay={handleEditRoleplay}
            handleStopSpeaking={handleStopSpeaking}
            courseId={courseId}
            sectionId={sectionId}
            topicId={topicId}
            navigate={navigate}
            handleSpeak={handleSpeak}
            clearQueue={clearQueue}
            stopAudio={stop}
            ttsWithoutSpeak={ttsWithoutSpeak}
            learnLang={learnLang}
            forLang={forLang}
            openSnackbar={openSnackbar}
            exerciseName="roleplay"
            handleTranslate={translate}
            handleComplete={handleCreateCompletion}
            viewAttempt={completedAttemptPath ? () => navigate(completedAttemptPath) : null}
            roleplayCreatedAtId={roleplayCreatedAtId}
            disableViewReport={disableViewReport}
            handleShareRoleplay={handleShareRoleplay}
            handleReportProblem={handleReportProblem}
            toggleMuteSound={toggleMuteSound}
            uploadAudioAttempt={uploadAudioAttempt}
            correctSound={correctSound}
            incorrectSound={incorrectSound}
            voices={
              roleplay && roleplay.data.learnLang
                ? config.languages[
                    roleplay.data.learnLang.toLowerCase() || "english"
                  ].Voices
                : null
            }
            roleplayProgress={roleplayProgressProps}
            // {...roleplayProgressProps}
            roleplayCompletions={roleplayCompletions}
            languageInformation={
              roleplay && roleplay.data.learnLang
                ? config.languages[
                    roleplay.data.learnLang.toLowerCase() || "english"
                  ]
                : null
            }
            authenticated={authenticated}
            handleSignIn={handleSignIn}
            courseSettings={courseSettings?.[courseId]}
            trackRecommendedEvent={trackRecommendedEvent}
            parentClassroom={parentClassroom}
            isReadWriteModeStudent={
              classroomMember?.assignedLabels?.isReadWriteModeStudent
            }
          />
        )}
    </Box>
  );
}

PlayRoleplay.propTypes = {
  navigate: PropTypes.func,
  authData: PropTypes.object,
};

export default PlayRoleplay;
